import {
  describeAnswer,
  type Readiness,
  type AssessmentStatus,
  type DisplayState,
  type QuestionDefinition,
  type StageState,
} from '@cyberscore/contracts';

/** What GET /assessments/:id/questions returns, one row per question in journey order. */
export interface QuestionRow {
  key: string;
  definition: QuestionDefinition;
  state: DisplayState;
  stage: { key: string; state: StageState; open: boolean } | null;
  answer: {
    response_id: string;
    value: unknown;
    provenance: string;
    channel: string;
    answered_at: string;
    answered_by: string | null;
  } | null;
  /** Send back when answering, so someone else's newer answer is never overwritten. */
  based_on: string | null;
}

export interface QuestionsView {
  assessment_id: string;
  status: AssessmentStatus;
  bundle: { id: string; content_sha256: string; platform_version: number; tenant_version: number | null };
  journey: {
    key: string;
    name: string;
    state: 'in_progress' | 'completed';
    next: { stage: string; question: string } | null;
  } | null;
  questions: QuestionRow[];
  /** The order every channel asks in (task 86): the journey's frame, the most valuable question first. */
  queue: string[];
  /** Whether there is enough for a reliable score yet (task 86). */
  readiness: Readiness;
}

/** An answer that counts: the question is shown (not hidden, and not counted as not in place by a route). */
export const counts = (r: QuestionRow) =>
  !!r.answer && (r.state.state === 'visible' || r.state.state === 'waiting');

/** Shown, asked of people (not filled by the system), and in a stage that is open. */
export function isAskable(r: QuestionRow): boolean {
  return r.state.state === 'visible' && r.state.ask && (!r.stage || r.stage.open);
}

/**
 * The question to ask next: the first of the core's queue (the most valuable question in the journey's
 * frame, task 86) that is open and unanswered; without one, the first unanswered mandatory question in
 * journey order, then optional ones. Questions the person chose to skip this session are passed over.
 */
export function nextQuestion(
  rows: QuestionRow[],
  skipped: ReadonlySet<string> = new Set(),
  queue: readonly string[] = [],
): string | null {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const queued = queue.find((k) => {
    const r = byKey.get(k);
    return r && isAskable(r) && !r.answer && !skipped.has(k);
  });
  if (queued) return queued;
  const open = rows.filter((r) => isAskable(r) && !r.answer && !skipped.has(r.key));
  return (open.find((r) => r.definition.mandatory) ?? open[0])?.key ?? null;
}

/** Mandatory questions still to answer (shown now, or waiting on an earlier answer), and roughly how long. */
export function remaining(rows: QuestionRow[]): { mandatory: number; minutes: number } {
  const mandatory = rows.filter(
    (r) =>
      r.definition.mandatory &&
      !r.answer &&
      r.stage?.state !== 'skipped' &&
      (r.state.state === 'waiting' || (r.state.state === 'visible' && r.state.ask)),
  ).length;
  // About 20 seconds a question, from the prototype's 12 questions in 3 minutes (rounded up).
  return { mandatory, minutes: Math.ceil((mandatory * 20) / 60) };
}

/** A short, human reading of an answer, for overviews and history. */
export const summarize = describeAnswer;

/** Answer errors from a 422, keyed by the field inside the value ("" for the value as a whole). */
export function valueErrors(issues: { path: string; message: string }[], index = 0): Record<string, string> {
  const out: Record<string, string> = {};
  const prefix = `answers.${index}`;
  for (const i of issues) {
    if (i.path !== prefix && !i.path.startsWith(`${prefix}.`)) continue;
    const rest = i.path.slice(prefix.length + 1); // "value.amount", "question", ""
    const field = rest.startsWith('value') ? rest.slice(6) : '';
    out[field] ??= plainMessage(i.message);
  }
  return out;
}

/** Validation messages in everyday words: "Too small: expected number to be >=0" reads "Enter 0 or more." */
export function plainMessage(m: string): string {
  const rules: [RegExp, (g: string[]) => string][] = [
    [/^Too small: expected number to be >=(\S+)$/, ([n]) => `Enter ${n} or more.`],
    [/^Too small: expected number to be >(\S+)$/, ([n]) => `Enter more than ${n}.`],
    [/^Too big: expected number to be <=(\S+)$/, ([n]) => `Enter ${n} or less.`],
    [/^Too big: expected number to be <(\S+)$/, ([n]) => `Enter less than ${n}.`],
    [/^Too small: expected string to have >=(\d+) characters?$/, ([n]) => n === '1' ? 'Enter an answer.' : `Use at least ${n} characters.`], // prettier-ignore
    [/^Too big: expected string to have <=(\d+) characters?$/, ([n]) => `Use at most ${n} characters.`],
    [/^Too small: expected array to have >=(\d+) items?$/, ([n]) => `Choose at least ${n}.`],
    [/^Too big: expected array to have <=(\d+) items?$/, ([n]) => `Choose at most ${n}.`],
    [/^Invalid input: expected \w+, received undefined$/, () => 'Enter an answer.'],
    [/^Invalid input: expected int, received number$/, () => 'Enter a whole number.'],
  ];
  for (const [re, say] of rules) {
    const g = re.exec(m);
    if (g) return say(g.slice(1));
  }
  return m;
}
